import { formatNormalizedAbv } from './beerIngestionSnapshot';

export const COMPARISON_FILTER_ALL = 'all';
export const COMPARISON_FILTER_NEEDS_REVIEW = 'needs_review';
export const COMPARISON_FILTER_NEW = 'new';
export const COMPARISON_FILTER_CHANGED = 'changed';
export const COMPARISON_FILTER_MISSING = 'missing';
export const COMPARISON_FILTER_INVALID = 'invalid';
export const COMPARISON_FILTER_APPLY_ERRORS = 'apply_errors';

export const COMPARISON_CLASSIFICATION_LABELS = {
  new: 'New',
  unchanged: 'Unchanged',
  changed_abv: 'Changed ABV',
  changed_style: 'Changed style',
  changed_description: 'Changed description',
  changed_multiple: 'Changed multiple',
  missing: 'Missing',
  invalid: 'Invalid',
};

const CHANGED_CLASSIFICATIONS = [
  'changed_abv',
  'changed_style',
  'changed_description',
  'changed_multiple',
];

/**
 * Human label for a comparison classification.
 *
 * @param {string} classification - Stored classification value
 * @returns {string}
 */
export function comparisonClassificationLabel(classification) {
  return COMPARISON_CLASSIFICATION_LABELS[classification] ?? classification ?? 'Unknown';
}

/**
 * Chip color for a comparison classification.
 *
 * @param {string} classification - Stored classification value
 * @returns {'default'|'success'|'warning'|'error'|'info'}
 */
export function comparisonClassificationColor(classification) {
  switch (classification) {
    case 'new':
      return 'info';
    case 'unchanged':
      return 'success';
    case 'missing':
      return 'default';
    case 'invalid':
      return 'error';
    case 'changed_abv':
    case 'changed_style':
    case 'changed_description':
    case 'changed_multiple':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * Whether a candidate classification counts as "changed" for filters.
 *
 * @param {string} classification - Stored classification value
 * @returns {boolean}
 */
export function isChangedComparisonClassification(classification) {
  return CHANGED_CLASSIFICATIONS.includes(classification);
}

/**
 * Whether a candidate still needs a human approve/reject decision.
 *
 * Missing observations stay visible but do not block complete or apply.
 *
 * @param {object|null|undefined} candidate - Comparison candidate
 * @returns {boolean}
 */
export function candidateRequiresHumanReview(candidate) {
  if (!candidate || candidate.classification === 'missing' || candidate.classification === 'unchanged') {
    return false;
  }
  return candidate.requiresReview === true;
}

/**
 * Filters comparison candidates by classification filter.
 *
 * @param {Array<object>} candidates - Comparison candidates
 * @param {string} filter - Active filter key
 * @returns {Array<object>}
 */
export function filterComparisonCandidates(candidates, filter = COMPARISON_FILTER_NEEDS_REVIEW) {
  const rows = Array.isArray(candidates) ? candidates : [];
  if (!filter || filter === COMPARISON_FILTER_ALL) {
    return rows;
  }
  if (filter === COMPARISON_FILTER_NEEDS_REVIEW) {
    return rows.filter((candidate) => (
      candidateRequiresHumanReview(candidate) || candidate.classification === 'missing'
    ));
  }
  if (filter === COMPARISON_FILTER_CHANGED) {
    return rows.filter((candidate) => isChangedComparisonClassification(candidate.classification));
  }
  return rows.filter((candidate) => candidate.classification === filter);
}

/**
 * Returns the current beer name for display.
 *
 * @param {object} candidate - Comparison candidate
 * @returns {string}
 */
export function comparisonCandidateCurrentName(candidate) {
  return candidate?.currentNormalized?.name
    ?? candidate?.baselineNormalized?.name
    ?? '—';
}

export const COMPARISON_FIELD_LABELS = {
  abv: 'ABV',
  style: 'Style',
  description: 'Description',
  name: 'Name',
};

export const DIFF_EQUAL = 'equal';
export const DIFF_REMOVED = 'removed';
export const DIFF_ADDED = 'added';

const MAX_COMPARISON_DIFF_TOKENS = 400;

/**
 * Human label for a comparison field key.
 *
 * @param {string} field - Stored field key
 * @returns {string}
 */
export function comparisonFieldLabel(field) {
  return COMPARISON_FIELD_LABELS[field] ?? field;
}

/**
 * Formats a field value for comparison display.
 *
 * @param {string} field - Field key
 * @param {object|null} normalized - Normalized beer snapshot row
 * @returns {string}
 */
export function formatComparisonFieldValue(field, normalized) {
  if (!normalized) {
    return '—';
  }
  if (field === 'abv') {
    const formatted = formatNormalizedAbv(normalized.abv);
    return formatted ?? 'missing';
  }
  if (field === 'style') {
    return normalized.style ?? 'missing';
  }
  if (field === 'description') {
    return normalized.description ?? 'missing';
  }
  if (field === 'name') {
    return normalized.name ?? 'missing';
  }
  return '—';
}

/**
 * Lists changed fields with display labels and previous/proposed values.
 *
 * @param {object} candidate - Comparison candidate
 * @returns {Array<{field: string, label: string, prior: string, proposed: string}>}
 */
export function listComparisonFieldChanges(candidate) {
  const changedFields = Array.isArray(candidate?.changedFields) ? candidate.changedFields : [];
  return changedFields.map((field) => ({
    field,
    label: comparisonFieldLabel(field),
    prior: formatComparisonFieldValue(field, candidate.baselineNormalized),
    proposed: formatComparisonFieldValue(field, candidate.currentNormalized),
  }));
}

/**
 * Builds a previous → proposed caption for changed fields.
 *
 * @param {object} candidate - Comparison candidate
 * @returns {string|null}
 */
export function formatComparisonFieldDelta(candidate) {
  const changes = listComparisonFieldChanges(candidate);
  if (changes.length === 0) {
    return null;
  }
  return changes
    .map((change) => `${change.field}: ${change.prior} → ${change.proposed}`)
    .join(' · ');
}

function tokenizeComparisonText(value) {
  if (typeof value !== 'string' || value.length === 0) {
    return [];
  }
  return value.split(/(\s+)/u).filter((token) => token.length > 0);
}

function createLcsLengthTable(leftTokens, rightTokens) {
  const rowCount = leftTokens.length;
  const columnCount = rightTokens.length;
  const table = [];
  for (let rowIndex = 0; rowIndex <= rowCount; rowIndex += 1) {
    const row = [];
    for (let columnIndex = 0; columnIndex <= columnCount; columnIndex += 1) {
      row.push(0);
    }
    table.push(row);
  }
  for (let leftIndex = rowCount - 1; leftIndex >= 0; leftIndex -= 1) {
    for (let rightIndex = columnCount - 1; rightIndex >= 0; rightIndex -= 1) {
      if (leftTokens[leftIndex] === rightTokens[rightIndex]) {
        table[leftIndex][rightIndex] = table[leftIndex + 1][rightIndex + 1] + 1;
      } else if (table[leftIndex + 1][rightIndex] >= table[leftIndex][rightIndex + 1]) {
        table[leftIndex][rightIndex] = table[leftIndex + 1][rightIndex];
      } else {
        table[leftIndex][rightIndex] = table[leftIndex][rightIndex + 1];
      }
    }
  }
  return table;
}

function appendDiffSegment(segments, type, text) {
  const lastSegment = segments[segments.length - 1];
  if (lastSegment && lastSegment.type === type) {
    lastSegment.text += text;
    return;
  }
  const startOffset = lastSegment
    ? lastSegment.startOffset + lastSegment.text.length
    : 0;
  segments.push({ type, text, startOffset });
}

function fallbackDiffSegments(priorText, proposedText) {
  const priorSegments = [];
  const proposedSegments = [];
  if (priorText) {
    appendDiffSegment(priorSegments, DIFF_REMOVED, priorText);
  }
  if (proposedText) {
    appendDiffSegment(proposedSegments, DIFF_ADDED, proposedText);
  }
  return { priorSegments, proposedSegments };
}

/**
 * Word-level diff of previous vs proposed comparison text.
 *
 * @param {string} priorText - Previous value
 * @param {string} proposedText - Proposed value
 * @returns {{priorSegments: Array<object>, proposedSegments: Array<object>}}
 */
export function diffComparisonText(priorText, proposedText) {
  const priorValue = typeof priorText === 'string' ? priorText : '';
  const proposedValue = typeof proposedText === 'string' ? proposedText : '';
  if (priorValue === proposedValue) {
    const equalSegments = [];
    if (priorValue) {
      appendDiffSegment(equalSegments, DIFF_EQUAL, priorValue);
    }
    return {
      priorSegments: equalSegments,
      proposedSegments: equalSegments.map((segment) => ({ ...segment })),
    };
  }
  const priorTokens = tokenizeComparisonText(priorValue);
  const proposedTokens = tokenizeComparisonText(proposedValue);
  if (
    priorTokens.length > MAX_COMPARISON_DIFF_TOKENS
    || proposedTokens.length > MAX_COMPARISON_DIFF_TOKENS
  ) {
    return fallbackDiffSegments(priorValue, proposedValue);
  }
  const table = createLcsLengthTable(priorTokens, proposedTokens);
  const priorSegments = [];
  const proposedSegments = [];
  let leftIndex = 0;
  let rightIndex = 0;
  while (leftIndex < priorTokens.length && rightIndex < proposedTokens.length) {
    if (priorTokens[leftIndex] === proposedTokens[rightIndex]) {
      appendDiffSegment(priorSegments, DIFF_EQUAL, priorTokens[leftIndex]);
      appendDiffSegment(proposedSegments, DIFF_EQUAL, proposedTokens[rightIndex]);
      leftIndex += 1;
      rightIndex += 1;
    } else if (table[leftIndex + 1][rightIndex] >= table[leftIndex][rightIndex + 1]) {
      appendDiffSegment(priorSegments, DIFF_REMOVED, priorTokens[leftIndex]);
      leftIndex += 1;
    } else {
      appendDiffSegment(proposedSegments, DIFF_ADDED, proposedTokens[rightIndex]);
      rightIndex += 1;
    }
  }
  while (leftIndex < priorTokens.length) {
    appendDiffSegment(priorSegments, DIFF_REMOVED, priorTokens[leftIndex]);
    leftIndex += 1;
  }
  while (rightIndex < proposedTokens.length) {
    appendDiffSegment(proposedSegments, DIFF_ADDED, proposedTokens[rightIndex]);
    rightIndex += 1;
  }
  return { priorSegments, proposedSegments };
}

/**
 * Summary counts from a comparison document or run row.
 *
 * @param {object} source - Comparison or run record
 * @returns {object}
 */
export function comparisonSummaryCounts(source = {}) {
  return {
    newCount: Number(source.candidatesNewCount) || 0,
    changedCount: Number(source.candidatesChangedCount) || 0,
    missingCount: Number(source.candidatesMissingCount) || 0,
    renameCount: Number(source.candidatesRenameCount) || 0,
    invalidCount: Number(source.candidatesInvalidCount) || 0,
    unchangedCount: Number(source.candidatesUnchangedCount) || 0,
  };
}
